import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Table, TableBody } from '@material-ui/core';
import { useDispatch } from 'react-redux';

import { getUtcDateDash, getUtcDateDashDifference } from 'modules/utils/time';

import { memberDetailActions } from 'features/member/reducer/memberDetailSlice';

import Type from './type/Type';
import Amount from './amount';
import Rate from './rate/Rate';
import CouponDate from './date';
import Context from './context/Context';
import CouponCode from './couponCode';
import InformModal from 'components/common/modal/InformModal';
import {
  CouponReason,
  CouponType,
  MAX_COUPON_AMOUNT,
  MAX_COUPON_RATE,
} from 'constants/member/connect';
import { SelectChangeEvent } from 'types/common/select';
import { CouponFormField } from 'types/member';

interface Props {
  userId: string;
  onRequiresValidation: (isValid: boolean) => void;
  closeModal: () => void;
}

const defaultInterval = [
  getUtcDateDash(new Date()),
  getUtcDateDashDifference(new Date(), 1, 'week', 'add'),
];

const ModalContent = forwardRef(
  ({ userId, onRequiresValidation, closeModal }: Props, modalContentRef) => {
    const dispatch = useDispatch();

    const [couponType, setCouponType] = useState<CouponType>(CouponType.PRICE);
    const [couponAmount, setCouponAmount] = useState<string>('');
    const [couponReason, setCouponReason] = useState<CouponReason>(CouponReason.EVENT);
    const [couponDescription, setCouponDescription] = useState<string>('');
    const [couponDateInterval, setCouponDateInterval] = useState<string[]>(defaultInterval);
    const [invalidDateModalOpen, setInvalidDateModalOpen] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const isEmpty = (value: string) => value.trim().length === 0;
    const isValidCouponAmount = (value: string) =>
      Number(value) > 0 && Number(value) <= MAX_COUPON_AMOUNT;
    const isValidCouponRate = (value: string) =>
      Number(value) > 0 && Number(value) <= MAX_COUPON_RATE;
    const isValidCouponValue = (type: CouponType, value: string) => {
      if (type === CouponType.PRICE) {
        return isValidCouponAmount(value);
      } else {
        return isValidCouponRate(value);
      }
    };

    const onChangeValue = (event: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent) => {
      const { value, name } = event.target;
      switch (name) {
        case CouponFormField.TYPE:
          setCouponType(Number(value) as CouponType);
          break;
        case CouponFormField.AMOUNT:
        case CouponFormField.RATE:
          setCouponAmount(value as string);
          break;
        case CouponFormField.DESCRIPTION:
          setCouponDescription(value as string);
          break;
        case CouponFormField.REASON:
          setCouponReason(Number(value) as CouponReason);
          break;
      }
    };

    useEffect(() => {
      const isValidCoupon = isValidCouponValue(couponType, couponAmount);
      const isDisabled = isEmpty(couponDescription) || !isValidCoupon;
      onRequiresValidation(isDisabled);
    }, [couponDescription, couponAmount, couponType]);

    useImperativeHandle(modalContentRef, () => ({
      saveNewCoupon: () => {
        if (!isValidCouponValue(couponType, couponAmount) || couponDescription.length === 0) {
          setErrorMessage('Amount and description are required.');
          setInvalidDateModalOpen(true);
          return;
        }

        dispatch(
          memberDetailActions.addNewCoupon({
            userId,
            amount: Number(couponAmount),
            type: couponType,
            dateInterval: couponDateInterval,
            reason: couponReason,
            description: couponDescription,
            reFetch: true,
          }),
        );

        resetState();
      },
    }));

    const resetState = () => {
      setCouponType(CouponType.PRICE);
      setCouponAmount('');
      setCouponDescription('');
      setCouponReason(CouponReason.EVENT);
      setCouponDateInterval(defaultInterval);
      closeModal();
    };

    return (
      <>
        <InformModal
          open={invalidDateModalOpen}
          onClose={() => setInvalidDateModalOpen(false)}
          description={errorMessage}
        />

        <Table size={'small'}>
          <TableBody>
            <Type couponType={couponType} onChangeValue={onChangeValue} />
            {couponType === CouponType.PRICE ? (
              <Amount couponAmount={couponAmount} onChangeValue={onChangeValue} />
            ) : (
              <Rate couponRate={couponAmount} onChangeValue={onChangeValue} />
            )}
            <CouponDate
              couponDateInterval={couponDateInterval}
              setCouponDateInterval={setCouponDateInterval}
            />
            <Context couponReason={couponReason} onChangeValue={onChangeValue} />
            <CouponCode couponDescription={couponDescription} onChangeValue={onChangeValue} />
          </TableBody>
        </Table>
      </>
    );
  },
);

export default ModalContent;
